import { getConfig } from "../constants/config";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { ethers } from "ethers-v5";
import { symphonyAddress } from "../../common/constants/contract";
import { verifySigner } from "../helpers/verifySigner";
import tokenService from "../../common/tokenService";

// Minimal ERC20 ABI for approval
const erc20ApproveAbi = [
  {
    constant: false,
    inputs: [
      { name: "_spender", type: "address" },
      { name: "_value", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
];

/**
 * Approves the Symphony contract to spend tokens on behalf of the user.
 * Supports both direct token approvals and route-based approvals.
 * Waits for block confirmations before considering the approval complete.
 *
 * @param {Object} params - Approval parameters
 * @param {(string|Array)} params.variable - Token address or route array
 *   - If string: Token address to approve (e.g., "0x123...")
 *   - If Array: Route array from getRoute
 * @param {ethers.Signer} params.signer - Signer for approval transaction
 * @param {(string|ethers.BigNumber)} [params.amount] - Amount to approve
 *   - Required when variable is a token address
 *   - Optional when variable is a route (defaults to route's amountIn if not provided)
 *   - When provided with a route, overrides the route's amount
 *   - If isRaw=true: Amount in raw units (wei)
 *   - If isRaw=false: Amount in formatted units (e.g., "1.0")
 * @param {Object} [params.options] - Additional options
 * @param {boolean} [params.options.isRaw=true] - If true, amount is in raw units
 * @param {number} [params.options.chainId=config.chainId] - Chain ID for approval
 * @param {number} [params.options.wait=1] - Number of block confirmations to wait
 *
 * @returns {Promise<string>} Transaction hash of the approval
 *
 * @throws {Error} If amount or token address is missing
 * @throws {Error} If token is not listed on Symphony (when using formatted amounts)
 * @throws {Error} If approval transaction fails
 *
 * @example
 * // Example 1: Approve tokens for a route (uses route's amount)
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const txHash = await giveApproval({
 *   variable: route,
 *   signer: provider.getSigner()
 * });
 *
 * @example
 * // Example 2: Approve custom amount for a route (overrides route's amount)
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const txHash = await giveApproval({
 *   variable: route,
 *   signer: provider.getSigner(),
 *   amount: "10.0", // Approve 10 WSEI instead of route's 1.0 WSEI
 *   options: { isRaw: false }
 * });
 *
 * @example
 * // Example 3: Approve specific token amount with custom confirmations
 * const txHash = await giveApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   signer: provider.getSigner(),
 *   amount: "1000000000000000000", // 1 WSEI in wei
 *   options: {
 *     isRaw: true,
 *     wait: 3 // Wait for 3 block confirmations
 *   }
 * });
 *
 * @example
 * // Example 4: Approve with formatted amount (direct token)
 * const txHash = await giveApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   signer: provider.getSigner(),
 *   amount: "1.0", // 1 WSEI
 *   options: { isRaw: false }
 * });
 */
export const giveApproval = async ({
  variable, // tokenAddress or route
  signer = undefined,
  amount,
  options = {
    isRaw: true,
    chainId: getConfig().chainId,
    wait: 1,
  },
}) => {
  let amountToApprove;
  let tokenToApprove;

  // Get config for non-token data
  const config = getConfig();
  const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
    config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  // If route given as variable
  if (Array.isArray(variable) && variable.length !== 0) {
    // Use provided amount if specified, otherwise extract from route
    if (amount !== undefined) {
      // When amount is provided, we need to determine if it's raw or formatted
      if (options.isRaw) {
        amountToApprove = amount;
      } else {
        // For formatted amount, we need token info to get decimals
        const tokenInAddress = getTokenIn(variable);
        const tokenInLower = tokenInAddress.toLowerCase();
        if (!tokens[tokenInLower]) {
          throw new Error(
            `Token ${tokenInAddress} data or decimals missing. This might mean token is not listed on Symphony.`
          );
        }
        amountToApprove = ethers.utils.parseUnits(
          amount.toString(),
          tokens[tokenInLower].attributes.decimals
        );
      }
    } else {
      // Use route's amount when no explicit amount provided
      amountToApprove = (await getTotalAmountIn(variable)).amountIn;
    }
    tokenToApprove = getTokenIn(variable);
  }

  // If tokenAddress is given as a variable
  if (
    !Array.isArray(variable) &&
    typeof variable === "string" &&
    variable.startsWith("0x") &&
    variable.length === 42
  ) {
    tokenToApprove = variable.toLowerCase();
    if (options.isRaw) amountToApprove = amount;
    if (!options.isRaw) {
      if (!tokens[tokenToApprove])
        throw new Error(
          `Token ${variable} data or decimals missing. This might mean token is not listed on Symphony.`
        );
      amountToApprove = ethers.utils.parseUnits(
        amount.toString(),
        tokens[tokenToApprove].attributes.decimals
      );
    }
  }

  if (!amountToApprove || !tokenToApprove)
    throw new Error(
      `Amount or token address is missing to approve. Check params passed to giveApproval. amountToApprove: ${amountToApprove},tokenToApprove: ${tokenToApprove}`
    );

  try {
    const verifiedSigner = await verifySigner(signer);

    // Create contract instance
    const contract = new ethers.Contract(
      tokenToApprove,
      erc20ApproveAbi,
      verifiedSigner
    );

    // Simulate the approval transaction before sending
    await contract.callStatic.approve(symphonyAddress, amountToApprove);

    // Send approval transaction
    const transaction = await contract.approve(
      symphonyAddress,
      amountToApprove
    );

    // Wait for transaction to be mined with 5 (default) confirmations
    const receipt = await transaction.wait(options.wait);

    if (receipt.status !== 1) {
      throw new Error("Approval transaction failed!");
    }

    return receipt;
  } catch (error) {
    console.error("Approval failed:", error.message);
    throw new Error(`Approve transaction failed. ${error.message}`);
  }
};
